import { alpha } from "@mui/material/styles";

export const darkPalette = {
  mode: "dark" as const,
  background: {
    default: "#0a0a0a",
    paper: "#1a1a1a",
  },
  primary: {
    main: "#ffffff",
    contrastText: "#000000",
  },
  text: {
    primary: "#ffffff",
    secondary: "#cccccc",
    disabled: "#888888",
  },
  divider: "#333333",
  action: {
    disabled: "#666666",
    disabledBackground: "#2a2a2a",
    hover: "#1a1a1a",
  },
};

export const lightPalette = {
  mode: "light" as const,
  background: {
    default: "#ffffff",
    paper: "#f8fafc",
  },
  primary: {
    main: "#000000",
    contrastText: "#ffffff",
  },
  text: {
    primary: "#1f2937",
    secondary: "#6b7280",
    disabled: "#9ca3af",
  },
  divider: "#e5e7eb",
};

export const getComponentOverrides = (isDark: boolean, palette: typeof darkPalette | typeof lightPalette) => ({
  MuiCssBaseline: {
    styleOverrides: {
      ".form-actions": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
        [`@media (min-width: 900px)`]: {
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        },
      },
      ".form-actions-center": {
        display: "flex",
        justifyContent: "center",
        gap: "16px",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        textDecoration: "none",
        borderRadius: 6,
      },
      containedPrimary: {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
        "&:hover": {
          backgroundColor: isDark ? "#f1f5f9" : "#1f2937",
        },
      },
      outlinedPrimary: {
        color: palette.primary.main,
        borderColor: isDark ? "#333333" : "#d1d5db",
        "&:hover": {
          borderColor: palette.primary.main,
          backgroundColor: isDark ? "#1a1a1a" : alpha(palette.primary.main, 0.04),
        },
      },
      textPrimary: {
        color: palette.primary.main,
        "&:hover": {
          backgroundColor: isDark ? "#1a1a1a" : alpha(palette.primary.main, 0.04),
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: isDark ? "#111111" : "#ffffff",
        borderBottom: `1px solid ${isDark ? "#333333" : "#e5e7eb"}`,
        color: palette.text.primary,
        boxShadow: "none",
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: isDark ? "#111111" : "#ffffff",
        borderRight: `1px solid ${isDark ? "#333333" : "#e5e7eb"}`,
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        "&:hover": {
          backgroundColor: isDark ? "#1a1a1a" : "#f3f4f6",
        },
        "&.Mui-selected": {
          backgroundColor: isDark ? "#1a1a1a" : "#f3f4f6",
          color: palette.primary.main,
          "&:hover": {
            backgroundColor: isDark ? "#2a2a2a" : "#e5e7eb",
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: isDark ? "#333333" : "#d1d5db",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: isDark ? "#444444" : "#9ca3af",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: palette.primary.main,
        },
        color: palette.text.primary,
      },
      input: {
        "&::placeholder": {
          color: isDark ? "#888888" : "#9ca3af",
          opacity: 1,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
        "&.Mui-focused": { color: palette.primary.main },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: 0,
        color: palette.text.secondary,
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        "& .MuiSwitch-switchBase.Mui-checked": {
          color: palette.primary.main,
          "& + .MuiSwitch-track": {
            backgroundColor: palette.primary.main,
          },
        },
        "& .MuiSwitch-track": {
          backgroundColor: isDark ? "#333333" : "#d1d5db",
        },
      },
    },
  },
});